import React, { useRef, useEffect, useState } from 'react';
import { ArrowRight, Star, Play, Quote, Clock, Users, CalendarCheck, Timer, ExternalLink, DollarSign, Heart, Home, Wrench } from 'lucide-react';
import SEO from "../components/SEO";
import { PAGE_SEO } from "../seo-data";
import { openBookingModal } from "../components/BookingModal";

const INDUSTRY_ICONS = {
  'Financial Services': DollarSign,
  'Healthcare': Heart,
  'Roofing & Renovations': Home,
  'Home Services': Wrench,
};

/* ─── Client Case Studies Data ─── */
const caseStudies = [
  {
    id: 'elite-buckeye',
    company: 'Elite Buckeye Renovations',
    name: 'Santiago Gonzalez',
    role: 'Owner',
    industry: 'Roofing & Renovations',
    logo: 'https://customer-assets.emergentagent.com/job_04ab2779-9e8b-4a1b-a708-4dc4c262039e/artifacts/c1jp47s4_image.png',
    stat: '40%',
    statLabel: 'Revenue Boost',
    highlight: '"Closed more jobs just by staying on top of every lead"',
    description: 'Elite Buckeye Renovations handles roofing, siding, and solar installations across Ohio. Santi was losing leads because there was no solid follow-up system in place.',
    solution: 'Skye AI automated the entire lead pipeline — voice and text follow-ups qualify leads and book them directly onto the calendar, hands-free.',
    metrics: {
      leadsRecovered: '83',
      responseTime: '97%',
      showRate: '82%',
      hoursSaved: '38',
    },
    reviewScreenshot: 'https://customer-assets.emergentagent.com/job_04ab2779-9e8b-4a1b-a708-4dc4c262039e/artifacts/26nuxcdx_Screenshot%202026-02-26%20at%207.38.22%E2%80%AFPM.png',
    reviews: [
      'Skye AI has been a game-changer for our roofing business! We\'ve been booking way more estimates without lifting a finger. The voice and text automations handle everything — from qualifying leads to getting them on our calendar. We\'ve closed more jobs just by staying on top of every lead.',
    ],
    trustpilotUrl: 'https://www.trustpilot.com/reviews/6868a01ce2b0a5a850844039',
    videos: [
      { src: 'https://embed-v2.testimonial.to/v/5d6d4537-451c-4809-bee6-29549178d68b?cc=on', label: 'Santi: Game-changer for roofing', landscape: false },
    ],
  },
  {
    id: 'trublue',
    company: 'TruBlue of North Idaho',
    name: 'TruBlue Ally',
    role: 'Franchise Owners',
    industry: 'Home Services',
    logo: 'https://customer-assets.emergentagent.com/job_04ab2779-9e8b-4a1b-a708-4dc4c262039e/artifacts/f1gfq0an_image.png',
    stat: '~3x',
    statLabel: 'Revenue Growth',
    highlight: '"The AI features & capabilities are amazing!"',
    description: 'TruBlue Home Service Ally is a national franchise offering handyman and senior home modification services across 110+ locations. They needed AI to handle customer calls and provide CRM analytics at scale.',
    solution: 'Skye AI built a custom AI CSR (Customer Service Representative) that handles inbound calls across locations, qualifies service requests, and feeds everything into an analytics-driven CRM.',
    metrics: {
      leadsRecovered: '210',
      responseTime: '99%',
      showRate: '88%',
      hoursSaved: '60',
    },
    reviewScreenshot: 'https://customer-assets.emergentagent.com/job_04ab2779-9e8b-4a1b-a708-4dc4c262039e/artifacts/m3lgj3jm_Screenshot%202026-02-26%20at%207.37.57%E2%80%AFPM.png',
    reviews: [
      'The team at SkyeAi have been phenomenal to serve my business needs with an AI CSR and the analytics in the CRM. They are quick to respond to my needs and questions. The AI features & capabilities are amazing!',
    ],
    trustpilotUrl: 'https://www.trustpilot.com/reviews/68704ad6e320596dd339eb8d',
    videos: [],
  },
  {
    id: 'fractional-sales',
    company: 'Fractional Sales',
    name: 'Tony Lopez',
    role: 'President & CEO',
    industry: 'Financial Services',
    logo: 'https://customer-assets.emergentagent.com/job_04ab2779-9e8b-4a1b-a708-4dc4c262039e/artifacts/rt5y2w3n_image.png',
    stat: '~3x',
    statLabel: 'Revenue Growth',
    highlight: '"AI is my best sales person"',
    description: 'Fractional Sales recruits top financial advisors for RIAs and broker dealers. Tony needed a way to engage prospects and qualify leads around the clock without expanding his team.',
    solution: 'Skye AI deployed a custom AI voice agent that handles inbound calls, qualifies leads, and books estimates — sounding so real that Tony\'s own team said "it\'s scary how real it sounds."',
    metrics: {
      leadsRecovered: '47',
      responseTime: '98%',
      showRate: '91%',
      hoursSaved: '32',
    },
    reviewScreenshot: 'https://customer-assets.emergentagent.com/job_04ab2779-9e8b-4a1b-a708-4dc4c262039e/artifacts/kiix6jrk_Screenshot%202026-02-26%20at%207.37.33%E2%80%AFPM.png',
    reviews: [
      'SkyeAI has a white glove service and is amazing in execution.',
      'I am very happy with my decision to use SkyeAi, their service is highly customized and we are getting great results. I highly recommend!',
    ],
    trustpilotUrl: 'https://www.trustpilot.com/reviews/6977eda07f663dfb8e4dc0b2',
    videos: [
      { src: 'https://embed-v2.testimonial.to/v/8c273542-f4e8-446a-8e7d-c12e815d8f01?cc=on', label: 'Tony: "AI is my best sales person"', landscape: true },
      { src: 'https://embed-v2.testimonial.to/v/29b74dd9-ab22-47e8-a28a-918bbd680e02?cc=on', label: 'Team reaction: "It sounds so real, it\'s scary"', landscape: true },
    ],
  },
  {
    id: 'greener-institute',
    company: 'The Greener Institute',
    name: 'Barry Greenberg',
    role: 'Owner',
    industry: 'Healthcare',
    logo: 'https://customer-assets.emergentagent.com/job_04ab2779-9e8b-4a1b-a708-4dc4c262039e/artifacts/ml9qhgbm_image.png',
    stat: '25%',
    statLabel: 'Revenue Increase',
    highlight: 'AI bot "Charli" handles patient comms — most don\'t realize they\'re speaking with AI',
    description: 'The Greener Institute helps patients obtain PA Medical Marijuana Cards. Barry needed a way to handle high call volume and keep patients informed without burning out his team.',
    solution: 'Skye AI built "Charli" — an AI assistant that communicates with patients with natural expression, manages the full intake process, and keeps the pipeline moving 24/7.',
    metrics: {
      leadsRecovered: '120',
      responseTime: '99%',
      showRate: '89%',
      hoursSaved: '45',
    },
    reviewScreenshot: 'https://customer-assets.emergentagent.com/job_04ab2779-9e8b-4a1b-a708-4dc4c262039e/artifacts/pu9f471v_Screenshot%202026-02-26%20at%207.36.49%E2%80%AFPM.png',
    reviews: [
      'I have researched many companies before pulling the trigger on Skye AI. They have delivered everything as promised! The system is awesome! My bot is called Charli and she provides full communication with my patients even with expression! Most don\'t even realize they are speaking with AI.',
    ],
    trustpilotUrl: 'https://www.trustpilot.com/reviews/68a1e7c445f2da12229d0912',
    videos: [
      { src: 'https://embed-v2.testimonial.to/v/a7ab9830-ee93-470e-bb05-25fb718c6e71?cc=on', label: 'Barry: 25% sales increase with Skye AI', landscape: true },
    ],
  },
];

/* Scroll-reveal hook */
const useScrollReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

const MetricsGrid = ({ metrics }) => (
  <div className="cs-metrics" data-testid="metrics-grid">
    <div className="cs-metric">
      <Users size={16} className="cs-metric__icon" />
      <span className="cs-metric__value">{metrics.leadsRecovered}</span>
      <span className="cs-metric__label">Leads Recovered / Mo</span>
    </div>
    <div className="cs-metric">
      <Timer size={16} className="cs-metric__icon" />
      <span className="cs-metric__value">{metrics.responseTime}</span>
      <span className="cs-metric__label">Faster Response</span>
    </div>
    <div className="cs-metric">
      <CalendarCheck size={16} className="cs-metric__icon" />
      <span className="cs-metric__value">{metrics.showRate}</span>
      <span className="cs-metric__label">Show Rate</span>
    </div>
    <div className="cs-metric">
      <Clock size={16} className="cs-metric__icon" />
      <span className="cs-metric__value">{metrics.hoursSaved}</span>
      <span className="cs-metric__label">Hours Saved / Mo</span>
    </div>
  </div>
);

const CaseStudyCard = ({ study, index }) => {
  const isEven = index % 2 === 0;
  const [ref, isVisible] = useScrollReveal();
  const IndustryIcon = INDUSTRY_ICONS[study.industry] || DollarSign;
  const revealDir = isEven ? 'cs-reveal--left' : 'cs-reveal--right';

  return (
    <section
      ref={ref}
      className={`cs-card ${isEven ? '' : 'cs-card--reversed'} ${isVisible ? 'cs-reveal--visible' : ''} ${revealDir}`}
      data-testid={`case-study-${study.id}`}
    >
      <div className="cs-card__watermark">
        <IndustryIcon size={180} strokeWidth={0.5} />
      </div>

      <div className="cs-card__info">
        <div className="cs-card__logo-wrap">
          <img src={study.logo} alt={study.company} className="cs-card__logo" data-testid={`logo-${study.id}`} />
        </div>
        <span className="cs-card__industry">{study.industry}</span>
        <h2 className="cs-card__company">{study.company}</h2>
        <p className="cs-card__person">{study.name}, {study.role}</p>

        <div className="cs-card__stat-block" data-testid={`stat-${study.id}`}>
          <div className="cs-card__stat-glow" />
          <span className="cs-card__stat-value">{study.stat}</span>
          <span className="cs-card__stat-label">{study.statLabel}</span>
        </div>

        <p className="cs-card__highlight">{study.highlight}</p>

        <MetricsGrid metrics={study.metrics} />

        <div className="cs-card__details">
          <div className="cs-card__detail">
            <h4>The Challenge</h4>
            <p>{study.description}</p>
          </div>
          <div className="cs-card__detail">
            <h4>The Solution</h4>
            <p>{study.solution}</p>
          </div>
        </div>
      </div>

      <div className="cs-card__media">
        {study.videos.map((video, i) => (
          <div key={i} className={`cs-card__video ${video.landscape ? 'cs-card__video--landscape' : 'cs-card__video--portrait'}`} data-testid={`video-${study.id}-${i}`}>
            <iframe
              src={video.src}
              frameBorder="0"
              scrolling="no"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={video.label}
            />
            <span className="cs-card__video-label">
              <Play size={12} /> {video.label}
            </span>
          </div>
        ))}

        <div className="cs-card__review-screenshot" data-testid={`review-screenshot-${study.id}`}>
          <img src={study.reviewScreenshot} alt={`${study.name} Trustpilot review`} />
        </div>

        {study.reviews.map((review, i) => (
          <div key={i} className="cs-card__review" data-testid={`review-${study.id}-${i}`}>
            <Quote size={18} className="cs-card__review-icon" />
            <p>{review}</p>
            <div className="cs-card__review-footer">
              <div className="cs-card__review-stars">
                {[...Array(5)].map((_, s) => <Star key={s} size={14} fill="#F59E0B" color="#F59E0B" />)}
              </div>
              <span className="cs-card__review-author">— {study.name}</span>
            </div>
            {study.trustpilotUrl && (
              <a href={study.trustpilotUrl} target="_blank" rel="noopener noreferrer" className="cs-card__review-link" data-testid={`trustpilot-link-${study.id}`}>
                Read on Trustpilot <ExternalLink size={12} />
              </a>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const CaseStudies = () => {
  return (
    <div className="case-studies-page" data-testid="case-studies-page">
      <SEO {...PAGE_SEO.caseStudies} />
      {/* Hero */}
      <section className="cs-hero">
        <div className="cs-hero__dot-pattern" />
        <div className="cs-hero__content">
          <span className="cs-hero__badge">Case Studies & Results</span>
          <h1 className="cs-hero__title" data-testid="cs-hero-title">$987K+ Revenue Generated.<br />Real Businesses. Real AI.</h1>
          <p className="cs-hero__subtitle">
            See how businesses are using Skye AI's custom voice agents, chatbots, and automations to drive real revenue and scale operations.
          </p>
        </div>
        <div className="cs-hero__stats">
          <div className="cs-hero__stat">
            <span>$987K+</span>
            <p>Revenue Generated</p>
          </div>
          <div className="cs-hero__stat">
            <span>300+</span>
            <p>Businesses Served</p>
          </div>
          <div className="cs-hero__stat">
            <span>24/7</span>
            <p>AI Systems Running</p>
          </div>
        </div>
      </section>

      {/* Client Case Studies */}
      <div className="cs-list">
        <div className="container" style={{ textAlign: 'center', padding: '60px 20px 20px' }}>
          <span className="cs-hero__badge">Client Success Stories</span>
          <h2 style={{ fontSize: '2rem', fontWeight: 700, marginTop: '16px', color: '#fff' }}>Real Businesses. Real Results.</h2>
        </div>
        {caseStudies.map((study, i) => (
          <React.Fragment key={study.id}>
            {i > 0 && <div className="cs-divider" />}
            <CaseStudyCard study={study} index={i} />
          </React.Fragment>
        ))}
      </div>

      {/* Bottom CTA */}
      <section className="cs-cta">
        <h2>Ready to see results like these?</h2>
        <p>Book a free AI Growth Planning Call and discover how custom AI can drive results like these for your business.</p>
        <button
          onClick={openBookingModal}
          className="cs-cta__btn"
          data-testid="case-studies-cta"
        >
          Book My AI Growth Planning Call
          <ArrowRight size={18} />
        </button>
      </section>
    </div>
  );
};

export default CaseStudies;
